<template>
  <long-query-layout
    resource-name="lion-c"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @query-completed="onQueryCompleted"
    @result-area-size-change="updateTableSize"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
        >詳細</gl-switch
      >
      <gl-switch
        :value="isGroupDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleGroup"
        >セグメント詳細</gl-switch
      >
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term-repeat v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <div>
          <md-radio v-model="queryFilter.item.selectMode" value="category">
            分類
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
          <md-radio v-model="queryFilter.item.selectMode" value="item">
            商品
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
        </div>
        <filter-item-category
          v-if="queryFilter.item.selectMode === 'category'"
          v-bind.sync="queryFilter.item"
        />
        <filter-item
          v-if="queryFilter.item.selectMode === 'item'"
          v-bind.sync="queryFilter.item"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗" contentWidth="medium">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option" title="オプション">
        <filter-option-lion-c v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermRepeat from '@/components/FilterTermRepeat.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterItem from '@/components/FilterItem2.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
import FilterOptionLionC from '@/components/FilterOptionLionC.vue'

import term from '@/queryfilter/termLion'
import item from '@/queryfilter/item2'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'

import GlTable from '@/components/GlTable.vue'

let hold = false

export default {
  components: {
    QueryFilterElement,
    FilterTermRepeat,
    FilterItemCategory,
    FilterItem,
    FilterStore,
    FilterCustomer,
    FilterOptionLionC,
    LongQueryLayout,
    GlTable,
  },
  props: {
    inherit: {
      type: String,
    },
  },
  data() {
    return {
      activeTab: undefined,
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: {
          order: 2,
          summaryKey: 'itemGroup',
          ...item.get(),
        },
        store: { order: 3, ...store.get() },
        customer: { order: 4, ...customer.get() },
        option: {
          order: 5,
          summaryKey: 'optionLionC',
          trialDays: 365,
          customerSegment: 'rank',
          errors: [],
        },
      },
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'sort_order',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          linkify: (rowData, cell, value, colName) => {
            cell.classList.add('customer-collection-cell')
            cell.setAttribute(
              'data-customer-collection',
              rowData.customer_filter[colName],
            )

            const toggle = e => {
              if (!this.resultTable.columns) {
                throw new Error('Column not found')
              }
              const col = this.resultTable.columns.find(
                c => c.colName === colName,
              )

              const id = e.target.getAttribute('data-customer-collection')
              const title =
                rowData.segment_name ||
                rowData.name ||
                rowData.company_name ||
                ''
              const state = rowData.state || ''
              const text = `${title}${state ? ` - ${state}` : ''} - ${
                col.headerText
              }`

              const customer = { id, text }

              this.$emit('toggle-customer-collection', customer)
            }

            cell.addEventListener('mousedown', e => {
              hold = true
              document.body.classList.add('no-select')
              toggle(e)
            })
            cell.addEventListener('mouseup', () => {
              hold = false
              document.body.classList.remove('no-select')
            })

            cell.addEventListener('mouseenter', e => {
              if (!hold) {
                return
              }
              toggle(e)
            })
          },
        },
      },
      customerCollection: [],
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        item: item.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
        option: {
          trialDays: queryFilter.option.trialDays,
          customerSegment: queryFilter.option.customerSegment,
        },
      }),
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        const p = item.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, queryFilter.customer)
        if (queryFilter.option.trialDays !== undefined) {
          this.queryFilter.option.trialDays = queryFilter.option.trialDays
        }
        if (queryFilter.option.customerSegment !== undefined) {
          this.queryFilter.option.customerSegment =
            queryFilter.option.customerSegment
        }
        return [p]
      },
      isDetail: false,
      isGroupDetail: false,
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
          showDetail: this.isDetail,
          showSegmentDetail: this.isGroupDetail,
        }
      },
    }
  },
  methods: {
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height
    },
    onQueryCompleted() {
      this.reloadTable()
    },
    toggleDetail(value) {
      this.isDetail = value
      this.reloadTable()
    },
    toggleGroup(value) {
      this.isGroupDetail = value
      this.reloadTable()
    },
    reloadTable() {
      this.updateResultUrlFlags()
      if (this.$refs.myTable) {
        this.$refs.myTable.init()
      }
    },
    updateResultUrlFlags() {
      if (!this.resultTable.url) {
        return
      }

      const [baseUrl, queryString] = this.resultTable.url.split('?')
      const params = new URLSearchParams(queryString || '')

      params.delete('showDetail')
      params.delete('showSegmentDetail')

      if (this.isDetail) {
        params.set('showDetail', 'true')
      }
      if (this.isGroupDetail) {
        params.set('showSegmentDetail', 'true')
      }

      const nextQuery = params.toString()
      this.resultTable.url = nextQuery ? `${baseUrl}?${nextQuery}` : baseUrl
    },
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
